"use client";

import React, { useState, useRef, useEffect } from "react";

const CHATBOT_TITLE = "CloudAxe";
const LOGO_URL = "/cloudaxe_onlyLogo_Transparent_NoBuffer.png";
const CATEGORIES = [
  { id: "ai-integrations", label: "AI Integrations" },
  { id: "apps", label: "Apps" },
  { id: "web-support", label: "Web Support" },
  { id: "hosting", label: "Hosting" },
] as const;

type CategoryId = (typeof CATEGORIES)[number]["id"];

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
}

export function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"filter" | "chat">("filter");
  const [category, setCategory] = useState<CategoryId | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Allow opening the chat from elsewhere (e.g. contact page) via custom event
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("cloudaxe-open-chat", handler);
    return () => window.removeEventListener("cloudaxe-open-chat", handler);
  }, []);

  const handleCategorySelect = (id: CategoryId) => {
    setCategory(id);
    setView("chat");
    const label = CATEGORIES.find((c) => c.id === id)?.label ?? id;
    setMessages([
      {
        id: crypto.randomUUID(),
        role: "assistant",
        content: `You're asking about **${label}**. How can I help? Ask anything about our ${label.toLowerCase()} services.`,
      },
    ]);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const chatHistory = [...messages, userMsg].map((m) => ({
        role: m.role,
        content: m.content,
      }));
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: chatHistory,
          category: category ?? undefined,
          useRag: true,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || `Request failed (${res.status})`);
      }

      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: data.message?.content ?? "I couldn't generate a reply. Please try again.",
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Something went wrong.";
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `Sorry, there was an error: ${message}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleBack = () => {
    setView("filter");
    setCategory(null);
    setMessages([]);
  };

  return (
    <>
      {/* Toggle button — bottom right; on mobile, just above the bottom nav (md: nav hidden) */}
      <div className="group fixed bottom-16 right-6 z-50 flex items-center md:bottom-6">
        {/* Tooltip — visible on hover when chat is closed */}
        {!open && (
          <span
            className="pointer-events-none absolute right-full mr-3 whitespace-nowrap rounded-xl bg-slate-800/95 px-4 py-2.5 text-sm font-medium tracking-wide text-white shadow-xl ring-1 ring-white/10 backdrop-blur-sm opacity-0 transition-opacity duration-200 delay-300 group-hover:opacity-100 dark:bg-white/95 dark:text-slate-900 dark:ring-slate-200/20"
            role="tooltip"
          >
            Chat with us
            {/* Arrow pointing at the button */}
            <span
              className="absolute right-0 top-1/2 h-3 w-3 -translate-y-1/2 translate-x-1/2 rotate-45 rounded-sm bg-slate-800/95 ring-1 ring-white/10 dark:bg-white/95 dark:ring-slate-200/20"
              aria-hidden
            />
          </span>
        )}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white shadow-lg ring-1 ring-white backdrop-blur-sm transition-all hover:bg-white/18 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2"
          aria-label={open ? "Close chat" : "Open chat"}
        >
          {open ? (
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={LOGO_URL} alt="" width={32} height={32} className="h-8 w-8 object-contain" />
          )}
        </button>
      </div>

      {/* Chat box — collapsible panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-40 flex h-[520px] w-[380px] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-2xl bg-[#27598e] shadow-2xl ring-1 ring-black/10"
          role="dialog"
          aria-label="Chat"
        >
          {/* Header — darker blue, avatar + title */}
          <div className="flex shrink-0 items-center gap-3 bg-[#4786c6] px-4 py-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20 text-white">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <span className="text-lg font-semibold text-white">{CHATBOT_TITLE}</span>
            {view === "chat" && (
              <button
                type="button"
                onClick={handleBack}
                className="ml-auto rounded p-1 text-white/80 hover:bg-white/20 hover:text-white"
                aria-label="Back to topic selection"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
            )}
          </div>

          {/* Main area — gradient + floating logo or content */}
          <div className="relative flex flex-1 flex-col overflow-hidden bg-gradient-to-b from-[#77a4d7] via-[#4786c6] to-[#27598e]">
            {/* Floating logo — behind content */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="animate-chatbot-logo-drift-x">
                  <div className="animate-chatbot-logo-drift-y">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={LOGO_URL}
                      alt=""
                      className="h-[280px] w-[280px] object-contain opacity-[0.12] select-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {view === "filter" && (
              <div className="relative z-10 flex flex-1 flex-col justify-center gap-4 p-5">
                <p className="text-center text-sm font-medium text-white/95">
                  What can we help you with?
                </p>
                <div className="flex flex-col gap-3">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => handleCategorySelect(c.id)}
                      className="rounded-xl bg-white/20 px-4 py-3 text-left text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {view === "chat" && (
              <div className="relative z-10 flex flex-1 flex-col">
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((m) => (
                    <div
                      key={m.id}
                      className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm text-white ${
                          m.role === "user"
                            ? "rounded-br-md bg-[#27598e]"
                            : "rounded-bl-md bg-[#77a4d7]/95"
                        }`}
                      >
                        <div className="whitespace-pre-wrap break-words">
                          {m.content.replace(/\*\*(.*?)\*\*/g, "$1")}
                        </div>
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="rounded-2xl rounded-bl-md bg-[#77a4d7]/95 px-4 py-2.5 text-sm text-white">
                        Thinking…
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Footer — input bar */}
                <div className="shrink-0 border-t border-white/10 bg-[#27598e] px-3 py-2">
                  <div className="flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Message"
                      className="min-w-0 flex-1 bg-transparent text-sm text-white placeholder:text-white/50 focus:outline-none"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="rounded p-1.5 text-white/60 hover:bg-white/10 hover:text-white"
                      aria-label="Attach photo"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="rounded p-1.5 text-white/60 hover:bg-white/10 hover:text-white"
                      aria-label="Share location"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="rounded p-1.5 text-white/60 hover:bg-white/10 hover:text-white"
                      aria-label="More options"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8v8m-16-8v8" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
